import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Confetti from "../Animations/Confetti";
import { dateFormatter } from "../../../utils/UtilityFunctions";

import "./pageloading.css";

/* No labels: a quote looks like a quote and a memory looks like a memory, so
   naming them only spends a line. The top line carries information instead —
   a memory's date, which anchors the entry before it is read rather than
   explaining it afterwards. */
const datelineFor = (content) => {
  if (content?.tileType === "memory" && content?.author) {
    return dateFormatter(content.author);
  }
  return null;
};

/* Memories are free text of any length. A 400-character entry must not render
   at the same size as a six-word quote. */
const lengthBand = (text = "") => {
  if (text.length > 260) return "long";
  if (text.length > 140) return "medium";
  return "short";
};

/* Said plainly, in the first person the rest of the controls use. "Don't show
   quotes" is a decision about a category, not about this one card, so the
   label names the category. */
const DISABLE_LABEL = {
  quotes: "Don't show quotes",
  memories: "Don't show memories",
};

const DISABLED_NOTICE = {
  quotes: "Quotes turned off.",
  memories: "Memories turned off.",
};

const renderBody = (content) => {
  const { tileType, text, author, authorLink, otherLink } = content;

  switch (tileType) {
    case "quote":
      return (
        <>
          <p className="nwd-loader__quote">{text}</p>
          <div className="nwd-loader__attribution">
            <span className="nwd-loader__author">
              {authorLink ? (
                <a href={authorLink} target="_blank" rel="noopener noreferrer">
                  {author}
                </a>
              ) : (
                author
              )}
            </span>
            {otherLink && (
              <a
                className="nwd-loader__discover"
                href={otherLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Discover more
              </a>
            )}
          </div>
        </>
      );
    case "memory":
      /* The date sits above, in the dateline. */
      return (
        <p className="nwd-loader__quote nwd-loader__quote--plain">{text}</p>
      );
    case "welcome":
    case "milestone":
      return (
        <>
          <p className="nwd-loader__quote nwd-loader__quote--plain">{text}</p>
          {author && <span className="nwd-loader__meta">{author}</span>}
        </>
      );
    case "backlog":
      /* The only tile that asks for something. The link belongs in the body
         rather than beside Hold and Skip: it navigates away, where those two
         govern the tile, and giving three different kinds of action one
         silhouette is what design.md §9 warns about. An internal Link, not an
         anchor — `otherLink` above opens a new tab, which would leave the app
         and lose the router. */
      return (
        <>
          <p className="nwd-loader__quote nwd-loader__quote--plain">{text}</p>
          {author && (
            <span className="nwd-loader__meta nwd-loader__meta--instruction">
              {author}
            </span>
          )}
          <Link
            className="nwd-loader__action nwd-loader__action--link"
            to="/my-account/journal?status=needs-score"
          >
            Show those days
          </Link>
        </>
      );
    default:
      return (
        <p className="nwd-loader__quote nwd-loader__quote--plain">{text}</p>
      );
  }
};

export const LoadingTile = ({
  content,
  hoverEvents,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  onTouchStart,
  isHeld = false,
  onHold,
  onSkip,
  canSkip = false,
  progress = null,
  onDisable,
  justDisabled = null,
  onUndo,
}) => {
  const tileType = content?.tileType || "loading";
  const showConfetti =
    (tileType === "welcome" || tileType === "milestone") &&
    content?.celebration;
  const isQuote = tileType === "quote";
  const dateline = datelineFor(content);
  /* Both sources switched off: there is nothing to read, so this is a loader
     and nothing more. See utils/loadingPreferences.js. */
  const isMinimal = Boolean(content?.suppressed);
  const hasControls = tileType !== "loading" && Boolean(onHold || onSkip);

  const [copied, setCopied] = useState(false);

  const handleCopyQuote = useCallback(() => {
    if (!isQuote || !content?.text || !navigator.clipboard) return;
    const toCopy = content.author
      ? `${content.text} — ${content.author}`
      : content.text;
    navigator.clipboard
      .writeText(toCopy)
      .then(() => setCopied(true))
      .catch(() => {});
  }, [isQuote, content]);

  /* Esc skips, Space holds — the keyboard equivalents of the control row. */
  useEffect(() => {
    if (!hasControls) return undefined;
    const onKeyDown = (event) => {
      const el = event.target;
      const isTyping =
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable);
      if (isTyping) return;

      if (event.key === "Escape" && onSkip) {
        event.preventDefault();
        onSkip();
      }
      /* Leave Space alone when a button has focus, or it would fire twice. */
      if (event.key === " " && onHold && el?.tagName !== "BUTTON") {
        event.preventDefault();
        onHold();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [hasControls, onSkip, onHold]);

  const surfaceEvents = {
    ...(hoverEvents || {}),
    onMouseEnter: onMouseEnter || hoverEvents?.onMouseEnter,
    onMouseLeave: (event) => {
      hoverEvents?.onMouseLeave?.(event);
      onMouseLeave?.(event);
      setCopied(false);
    },
    onFocus,
    onBlur,
    onTouchStart,
  };

  return (
    <div className="nwd-loader">
      {showConfetti && <Confetti />}
      <article
        {...surfaceEvents}
        className="nwd-loader__card"
        data-tile-type={tileType}
        data-length={lengthBand(content?.text)}
        data-minimal={isMinimal ? "true" : "false"}
      >
        {/* One element, two jobs: an indeterminate bar while the page is still
            fetching, and the dwell countdown once content lands. It rides the
            card's top edge — it reports on the card as a whole, so it does not
            belong down among the buttons that act on it. */}
        <span
          className="nwd-loader__progress"
          data-held={isHeld ? "true" : "false"}
          data-indeterminate={progress === null ? "true" : "false"}
          aria-hidden="true"
        >
          <span
            className="nwd-loader__progress-bar"
            style={
              progress === null
                ? undefined
                : { transform: `scaleX(${progress})` }
            }
          />
        </span>

        <div className="nwd-loader__content" role="status" aria-live="polite">
          {justDisabled ? (
            <>
              <p className="nwd-loader__quote nwd-loader__quote--plain">
                {DISABLED_NOTICE[justDisabled]}
              </p>
              <span className="nwd-loader__meta">
                Turn them back on any time in{" "}
                <a
                  className="nwd-loader__notice-link"
                  href="/my-account/preferences"
                >
                  account preferences
                </a>
                .
              </span>
            </>
          ) : (
            <>
              {dateline && <p className="nwd-loader__dateline">{dateline}</p>}
              {renderBody(content)}
            </>
          )}
        </div>

        {justDisabled && onUndo && (
          <div className="nwd-loader__actions">
            <button
              type="button"
              className="nwd-loader__action"
              onClick={onUndo}
            >
              Undo
            </button>
          </div>
        )}

        {!justDisabled && hasControls && (
          <div className="nwd-loader__actions">
            {isQuote && (
              <button
                type="button"
                className="nwd-loader__action"
                onClick={handleCopyQuote}
              >
                {copied ? "Copied" : "Copy"}
              </button>
            )}
            {onHold && (
              <button
                type="button"
                className="nwd-loader__action"
                onClick={onHold}
                aria-pressed={isHeld}
              >
                {isHeld ? "Resume" : "Hold"}
              </button>
            )}
            {/* Quieter than Hold and Skip on purpose: it is a lasting
                decision sitting next to two momentary ones, and §9 warns
                against one silhouette serving every kind of action. */}
            {onDisable && (
              <button
                type="button"
                className="nwd-loader__action nwd-loader__action--mute"
                onClick={onDisable}
              >
                {DISABLE_LABEL[isQuote ? "quotes" : "memories"]}
              </button>
            )}
            {onSkip && (
              <button
                type="button"
                className="nwd-loader__action nwd-loader__action--skip"
                onClick={onSkip}
                disabled={!canSkip}
              >
                Skip
              </button>
            )}
          </div>
        )}
      </article>
    </div>
  );
};

export default LoadingTile;
